import { Link } from "@tanstack/react-router";
import { ChevronDown, Menu } from "lucide-react";
import { useState } from "react";

import { BrandLogo } from "@/components/brand-logo";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { CRUKS_NAV, MEGA_MENU, PAYOUT_NAV, PRIMARY_NAV, SITE } from "@/lib/site";

export function SiteHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="bg-primary px-4 py-1.5 text-center text-xs font-medium text-primary-foreground">
        18+ | Speel bewust. Kansspelen kunnen verslavend zijn. Hulp:{" "}
        <a href="https://www.loketkansspel.nl" target="_blank" rel="nofollow noopener noreferrer" className="underline">
          Loket Kansspel
        </a>
      </div>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2 font-bold text-foreground">
          <BrandLogo className="h-9 w-9" />
          <span className="text-lg tracking-tight">{SITE.name}</span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Hoofdmenu">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium">
                  Betaalmethoden
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[860px] grid-cols-4 gap-6 p-6">
                    {MEGA_MENU.map((group) => (
                      <div key={group.title}>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {group.title}
                        </p>
                        <ul className="space-y-1">
                          {group.links.map((link) => (
                            <li key={link.href}>
                              <NavigationMenuLink asChild>
                                <Link
                                  to={link.href}
                                  className="block rounded-md px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                                >
                                  {link.label}
                                  {link.draft ? (
                                    <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">
                                      binnenkort
                                    </span>
                                  ) : null}
                                </Link>
                              </NavigationMenuLink>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
              {PAYOUT_NAV.length > 0 ? (
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="text-sm font-medium">
                    Uitbetalen
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="w-[280px] p-3">
                      {PAYOUT_NAV.map((link) => (
                        <li key={link.href}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={link.href}
                              className="block rounded-md px-2 py-2 text-sm text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                            >
                              {link.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              ) : null}
            </NavigationMenuList>
          </NavigationMenu>
          <Link
            to={CRUKS_NAV.href}
            className="rounded-md bg-accent px-3 py-2 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/80"
            activeProps={{ className: "ring-1 ring-primary" }}
          >
            {CRUKS_NAV.label}
          </Link>
          {PRIMARY_NAV.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="rounded-md px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button asChild className="hidden bg-success text-success-foreground hover:bg-success/90 lg:inline-flex">
            <Link to="/casinozoeker/">
              Vind je casino
              <ChevronDown className="ml-1 h-4 w-4 rotate-[-90deg]" aria-hidden />
            </Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="lg:hidden" aria-label="Menu openen">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <nav className="mt-6 space-y-6" aria-label="Mobiel menu">
                <Link
                  to={CRUKS_NAV.href}
                  onClick={() => setOpen(false)}
                  className="block rounded-md bg-accent px-3 py-2 text-sm font-semibold text-accent-foreground"
                >
                  {CRUKS_NAV.label}
                </Link>
                {MEGA_MENU.map((group) => (
                  <div key={group.title}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.title}
                    </p>
                    <ul className="space-y-1">
                      {group.links.map((link) => (
                        <li key={link.href}>
                          <Link
                            to={link.href}
                            onClick={() => setOpen(false)}
                            className="block rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {PAYOUT_NAV.length > 0 ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Uitbetalen
                    </p>
                    <ul className="space-y-1">
                      {PAYOUT_NAV.map((link) => (
                        <li key={link.href}>
                          <Link
                            to={link.href}
                            onClick={() => setOpen(false)}
                            className="block rounded-md px-2 py-1.5 text-sm text-foreground hover:bg-accent"
                          >
                            {link.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="border-t border-border pt-4">
                  {PRIMARY_NAV.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className="block rounded-md px-2 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
